import {
    useEffect, useState, useCallback
} from 'react';
import {
    useParams
} from 'react-router-dom';

import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    apiManagerService
} from 'shared/api/services/BEX/apiManager.service';
import {
    mcpConnectionService
} from 'shared/api/services/BEX/mcpConnection.service';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    redisCollectionService
} from 'shared/api/services/BEX/redisCollection.service';
import {
    channelService
} from 'shared/api/services/BEX/channel.service';
import {
    opcodeAssistantService
} from 'shared/api/services/BEX/opcodeAssistant.service';

/**
 * useResourcePickerData — single hook that loads "items of kind K" for the
 * current sub-org and returns a uniform list shape:
 *
 *   { id, label, sublabel?, badge?, raw }
 *
 * - `id`        : the value the consumer should bind to (selection key).
 * - `label`     : primary visible name.
 * - `sublabel`  : short detail line (model/path/url/etc).
 * - `badge`     : small pill text (provider, kind, status…).
 * - `raw`       : the unprocessed record (so chips can read extra fields).
 *
 * Service-backed kinds: llm · api · mcp · opcode · collection · channel · assistant.
 * Local-prop kinds (no fetch — caller passes `localItems`):
 *   definition · flow · step.
 *
 * Returns: { items, loading, error, refetch }
 */
function unwrapList(res) {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.connections)) return res.connections;
    return [];
}

const SERVICE_KINDS = new Set([
    'llm', 'api', 'mcp', 'opcode', 'collection', 'channel', 'assistant'
]);
const LOCAL_KINDS = new Set(['definition', 'flow', 'step']);

function normalize(kind, row) {
    if (!row || typeof row !== 'object') return null;
    switch (kind) {
        case 'llm': {
            // selection key is a tuple — encode as `unique_name::model_type`.
            if (!row.unique_name || !row.model_type) return null;
            return {
                id: `${row.unique_name}::${row.model_type}`,
                label: row.unique_name,
                sublabel: row.modelId || row.model_id || '',
                badge: row.model_type,
                raw: row
            };
        }
        case 'api': {
            const id = row.api_id || row._id || row.id;
            if (!id) return null;
            return {
                id,
                label: row.api_name || row.name || id,
                sublabel: `${row.method || 'GET'} · ${row.url || row.endpoint || ''}`,
                badge: row.method || 'API',
                raw: row
            };
        }
        case 'mcp': {
            const id = row.connection_id || row._id;
            if (!id) return null;
            return {
                id,
                label: row.connection_name || id,
                sublabel: row.server_url || '',
                badge: row.auth?.type || 'none',
                raw: row
            };
        }
        case 'opcode': {
            const id = row.opcode_id || row._id;
            if (!id) return null;
            return {
                id,
                label: row.title || id,
                sublabel: row.description || '',
                badge: row.version || 'v?',
                raw: row
            };
        }
        case 'collection': {
            const id = row.collection_id || row._id;
            if (!id) return null;
            return {
                id,
                label: row.collection_name || row.name || id,
                sublabel: row.embedding_model || row.description || '',
                badge: 'KB',
                raw: row
            };
        }
        case 'channel': {
            const id = row._id || row.channel_id;
            if (!id) return null;
            return {
                id,
                label: row.topic || row.name || row.channel_name || id,
                sublabel: row.address || row.kind || '',
                badge: row.kind || row.type || 'channel',
                raw: row
            };
        }
        case 'assistant': {
            const id = row.assistant_id || row._id;
            if (!id) return null;
            return {
                id,
                label: row.title || id,
                sublabel: row.description || row.persona || '',
                badge: row.status || row.version || 'draft',
                raw: row
            };
        }
        case 'definition':
        case 'flow':
        case 'step': {
            const id = row.id || row.ref;
            if (!id) return null;
            return {
                id,
                label: id,
                sublabel: row.description || '',
                badge: row.kind || kind,
                raw: row
            };
        }
        default:
            return null;
    }
}

async function fetchKind(kind, oragID, subOragID) {
    switch (kind) {
        case 'llm':
            return unwrapList(await instructionService.listModels(oragID, subOragID, null));
        case 'api':
            return unwrapList(await apiManagerService.listApis(oragID, subOragID));
        case 'mcp':
            return unwrapList(await mcpConnectionService.listMCPConnections(oragID, subOragID));
        case 'opcode':
            return unwrapList(await jsonOpcodeService.listJSONOpcodes({
                organizationId: oragID,
                subOrganizationId: subOragID
            }));
        case 'collection':
            return unwrapList(await redisCollectionService.listCollections(oragID, subOragID));
        case 'channel':
            return unwrapList(await channelService.getChannels(oragID, subOragID));
        case 'assistant':
            return unwrapList(await opcodeAssistantService.listOpcodeAssistants(oragID, subOragID));
        default:
            return [];
    }
}

export default function useResourcePickerData(kind, opts = {}) {
    const {
        oragID, subOragID
    } = useParams();
    const {
        localItems, enabled = true
    } = opts;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const isService = SERVICE_KINDS.has(kind);
    const isLocal = LOCAL_KINDS.has(kind);

    const refetch = useCallback(async () => {
        if (!enabled) return;
        if (isLocal) {
            const arr = Array.isArray(localItems) ? localItems : [];
            setItems(arr.map((r) => normalize(kind, r)).filter(Boolean));
            return;
        }
        if (!isService) {
            setItems([]);
            return;
        }
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        try {
            const arr = await fetchKind(kind, oragID, subOragID);
            setItems(arr.map((r) => normalize(kind, r)).filter(Boolean));
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load.');
        } finally {
            setLoading(false);
        }
    }, [kind, oragID, subOragID, isService, isLocal, localItems, enabled]);

    useEffect(() => {
        let cancelled = false;
        if (!enabled) return undefined;
        if (isLocal) {
            const arr = Array.isArray(localItems) ? localItems : [];
            setItems(arr.map((r) => normalize(kind, r)).filter(Boolean));
            return undefined;
        }
        if (!isService) {
            setItems([]);
            return undefined;
        }
        if (!oragID || !subOragID) return undefined;
        setLoading(true);
        setError(null);
        fetchKind(kind, oragID, subOragID)
            .then((arr) => {
                if (cancelled) return;
                setItems(arr.map((r) => normalize(kind, r)).filter(Boolean));
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.response?.data?.message || err?.message || 'Failed to load.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [kind, oragID, subOragID, isService, isLocal, localItems, enabled]);

    return {
        items,
        loading,
        error,
        refetch
    };
}

export { normalize as normalizeResourceRow };
